import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';

// Configure axios base URL for production
if (process.env.NODE_ENV === 'production') {
  axios.defaults.baseURL = '';
}

function ProductSearch({ authData }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [distributorDetails, setDistributorDetails] = useState(null);
  const [loadingDistributor, setLoadingDistributor] = useState(false);

  const searchProducts = async (query) => {
    if (!query.trim()) {
      setProducts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`/api/products/search`, {
        size: 48,
        query: {
          match: {
            name: {
              query: query,
              fuzziness: "AUTO"
            }
          }
        },
        _source: [
          "product_id", "name", "manufacturers", "salt_composition", 
          "medicine_type", "introduction", "benefits", "description", 
          "how_to_use", "safety_advise", "if_miss", "packaging_detail", 
          "mrp", "prescription_required", "fact_box", "primary_use", 
          "storage", "common_side_effect", "alcohol_interaction", 
          "pregnancy_interaction", "lactation_interaction", "driving_interaction", 
          "kidney_interaction", "liver_interaction", "manufacturer_address", 
          "q_a", "how_it_works", "reference", "image_url", "item_code", 
          "dist_item_code", "package", "qty", "product_form", "gst_rate", 
          "hsn_code", "product_category_name", "product_sub_category_name",
          "distributor", "selling_price_incl_gst", "effective_customer_discount",
          "inventory_quantity", "deferred_allowed", "is_active"
        ]
      }, {
        headers: {
          phone: authData.phone,
          otp: authData.otp
        }
      });

      const hits = response.data.hits?.hits || [];
      const productData = hits.map(hit => hit._source);
      console.log('Total hits:', response.data.hits?.total?.value);
      console.log('Hits returned:', hits.length);
      console.log('Products set:', productData.length);
      setProducts(productData);
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchProducts(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchDistributorDetails = async (itemCode) => {
    if (!itemCode) {
      setDistributorDetails(null);
      return;
    }

    setLoadingDistributor(true);
    try {
      const response = await axios.get(`/api/distributor/${encodeURIComponent(itemCode)}`, {
        headers: {
          phone: authData.phone,
          otp: authData.otp
        }
      });
      setDistributorDetails(response.data.data);
    } catch (error) {
      console.error('Error fetching distributor details:', error);
      setDistributorDetails(null);
    } finally {
      setLoadingDistributor(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    // Fetch distributor details if item_code is available
    const itemCode = product.item_code || product.dist_item_code;
    fetchDistributorDetails(itemCode);
  };

  const closeModal = () => {
    setSelectedProduct(null);
    setDistributorDetails(null);
  };

  return (
    <div className="container">
      <div className="search-container">
        <h1>Product Search</h1>
        <input
          type="text"
          className="search-input"
          placeholder="Search products by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loading && <div className="loading">Searching...</div>}
      
      {error && <div className="error">{error}</div>}

      {!loading && !error && products.length > 0 && (
        <div className="products-grid">
          {products.map((product) => (
            <ProductCard
              key={product.product_id}
              product={product}
              onClick={() => handleProductClick(product)}
            />
          ))}
        </div>
      )}

      {!loading && !error && searchQuery && products.length === 0 && (
        <div className="loading">No products found</div>
      )}

      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          distributorDetails={distributorDetails}
          loadingDistributor={loadingDistributor}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default ProductSearch; 